import { useCallback, useEffect, useRef } from 'react';
import { FieldErrors, FieldPath, useForm } from 'react-hook-form';
import {
  CodeAcceptanceStatus,
  ContactDetailsType,
  FINANCIAL_PRODUCT,
  VerificationType,
  getContactDetailsSchema,
} from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert } from '@spark-web/alert';
import { Button } from '@spark-web/button';
import { Container } from '@spark-web/container';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { usePasswordlessUserVerification, useUpdateContactDetails } from '../hooks';
import {
  AppMetadata,
  LocalStorageCache,
  getFormattedAppMetadataCacheKey,
  getFormattedAuthCacheKey,
  useAuth0,
} from '../provider';
import { LABELS, isValidAustralianMobile, parseAustralianPhoneNumber } from '../utils';
import { ContactVerification } from './ContactVerification';
import { SecureFields } from './SecureCode';
import { UpdateFields } from './UpdateFields';
import { ValidationFields } from './ValidationFields';

const { CONTINUE, REQUEST_CODE, CONFIRM_MOBILE, CONFIRM_EMAIL, WRONG_EMAIL_OTP, WRONG_MOBILE_OTP, MAX_ATTEMPTS } =
  LABELS;

export type FormContactFieldsType = ContactDetailsType & SecureFields;

export type VerificationDetailsType = {
  email?: string;
  mobile?: string;
  verificationType?: VerificationType;
  maskedEmail?: string;
  maskedMobile?: string;
};

type ContactFieldsProps = {
  verificationDetails?: VerificationDetailsType;
  onError: (error: unknown) => void;
  onSuccess?: ({ verifiedEmail, verifiedMobile }: { verifiedEmail?: string; verifiedMobile: string }) => void;
  onTracking?: () => void;
  financialProductId?: string;
  v2Flow?: boolean;
  isUniversalLogin?: boolean;
};

export const ContactFields = ({
  onError,
  onSuccess,
  onTracking,
  verificationDetails,
  financialProductId,
  v2Flow = false,
  isUniversalLogin = false,
}: ContactFieldsProps) => {
  const { setVerificationCompleted, setCurrentSession } = useAuth0();

  const contactDetailsSchema = getContactDetailsSchema(isUniversalLogin);

  // Normalize mobile numbers to ensure they start with 0
  const normalizedMobile = parseAustralianPhoneNumber(verificationDetails?.mobile, false);
  const isMobileInvalid = !isValidAustralianMobile(verificationDetails?.mobile);

  const formContext = useForm<FormContactFieldsType>({
    resolver: zodResolver(contactDetailsSchema),
    defaultValues: {
      ...verificationDetails,
      mobile: isMobileInvalid ? normalizedMobile : verificationDetails?.mobile,
      verificationType: verificationDetails?.verificationType ?? VerificationType.Sms,
      newEmail: verificationDetails?.email,
      newMobile: isMobileInvalid ? normalizedMobile : verificationDetails?.mobile,
      otp: '',
      otpSent: false,
      codeAccepted: CodeAcceptanceStatus.NotAccepted,
      updateOtpSent: false,
    },
  });

  const { control, getValues, register, setError, setFocus, setValue, watch, handleSubmit } = formContext;

  const {
    createPasswordlessUser,
    createPasswordlessUserResponse,
    createPasswordlessUserLoading,
    errorCreatePasswordlessUser,

    authenticatePasswordlessUser,
    authenticatePasswordlessUserResponse,
    authenticatePasswordlessLoading,
    authenticatePasswordlessUserError,
  } = usePasswordlessUserVerification();

  const {
    sendUpdateContactDetailsVerification,
    errorField,
    errorSendUpdateContactDetailsVerification,
    verifyUpdateContactDetails,
    errorVerifyUpdateContactDetails,
    isLoading: isLoadingUpdateDetails,
    verifyUpdateContactDetailsResponse,
    isRequestLoading,
  } = useUpdateContactDetails();

  const verificationType =
    verificationDetails?.verificationType === VerificationType.Email || isMobileInvalid
      ? VerificationType.Email
      : verificationDetails?.verificationType;

  const isMobileAndEmail = verificationType === VerificationType.SmsEmail;
  const isMobileOnly = verificationType === VerificationType.Sms;
  const isEmailOnly = verificationType === VerificationType.Email;

  const [otpSent, updateOtpSent, codeAccepted, otp] = watch(['otpSent', 'updateOtpSent', 'codeAccepted', 'otp']);

  const buildLogContext = useCallback(
    (extra: Record<string, unknown> = {}) => {
      const [email, mobile, newEmail, newMobile, verificationType] = getValues([
        'email',
        'mobile',
        'newEmail',
        'newMobile',
        'verificationType',
      ]);

      return {
        verificationType,
        hasEmail: !!email,
        hasMobile: !!mobile,
        hasNewEmail: !!newEmail,
        hasNewMobile: !!newMobile,
        otpSent,
        updateOtpSent,
        codeAccepted,
        ...extra,
      };
    },
    [codeAccepted, getValues, otpSent, updateOtpSent],
  );

  // Tracks the auth/create error object already reported to Datadog so a single failure isn't
  // re-logged on every re-render while the error persists in state (until the next attempt clears it).
  const loggedVerificationErrorRef = useRef<unknown>(null);

  const existingCookie = isMobileAndEmail
    ? (() => {
        const lsCache = new LocalStorageCache();
        const key = parseAustralianPhoneNumber(verificationDetails?.mobile);
        const metadata = lsCache.get<AppMetadata>(getFormattedAppMetadataCacheKey(key));
        // Only trust metadata if auth credentials also exist — otherwise it's stale
        return metadata?.verificationCompleted && lsCache.get(getFormattedAuthCacheKey(key)) ? metadata : undefined;
      })()
    : undefined;

  useEffect(() => {
    if (verificationDetails?.mobile) {
      setCurrentSession(verificationDetails?.mobile);
    }
  }, []);

  useEffect(() => {
    setValue('otp', otp);

    // This effect is to handle the response from the passwordless authentication API - createPasswordlessUser
    if (createPasswordlessUserResponse) {
      setValue('otpSent', true);
      datadogRum.addAction('Contact verification request code success', buildLogContext());
    }

    // This handles when user has both mobile and email correct so we don't have to update user details
    if (authenticatePasswordlessUserResponse && isMobileAndEmail) {
      setVerificationCompleted(otp, verificationDetails?.mobile);
      onAuthSuccess();
    }

    if (authenticatePasswordlessUserResponse && verificationType) {
      setValue('codeAccepted', CodeAcceptanceStatus[verificationType]);
    }

    // This sets email as a value if it's empty and we have verified email
    if (authenticatePasswordlessUserResponse?.email && !verificationDetails?.email) {
      setValue('email', authenticatePasswordlessUserResponse.email);
    }

    // If error occurs during passwordless authentication
    if (authenticatePasswordlessUserError || errorCreatePasswordlessUser) {
      const getErrorCode = (error: unknown) =>
        (error as any)?.response?.errors?.[0]?.extensions?.response?.body?.code as string;

      // Wrong/expired-code and max-attempt responses are expected user input errors, not system
      // faults, so report them as actions rather than errors to keep error tracking signal meaningful.
      // The error code is derived per error object so each report is classified on its own response.
      const reportVerificationFailure = (message: string, error: unknown) => {
        const errorCode = getErrorCode(error);
        const context = buildLogContext({ errorCode });
        if (errorCode === WRONG_EMAIL_OTP || errorCode === WRONG_MOBILE_OTP || errorCode === MAX_ATTEMPTS) {
          datadogRum.addAction(message, context);
        } else {
          datadogRum.addError(new Error(message), context);
        }
      };

      // Only report each distinct error object once — the effect re-runs on unrelated dependency
      // changes while the error stays in state, which would otherwise log the same failure repeatedly.
      const verificationError = authenticatePasswordlessUserError || errorCreatePasswordlessUser;
      if (verificationError !== loggedVerificationErrorRef.current) {
        loggedVerificationErrorRef.current = verificationError;

        if (errorCreatePasswordlessUser) {
          reportVerificationFailure('Contact verification request code failed', errorCreatePasswordlessUser);
        }
        if (authenticatePasswordlessUserError) {
          reportVerificationFailure('Contact verification authenticate failed', authenticatePasswordlessUserError);
        }
      }

      const errorMessage = getErrorCode(verificationError);
      setError(
        'otp',
        {
          // TO DO: the error code are coming from backend serverless and they not GQL standard
          message: errorMessage,
        },
        {
          shouldFocus: true,
        },
      );
      if (errorMessage !== WRONG_EMAIL_OTP && errorMessage !== WRONG_MOBILE_OTP && errorMessage !== MAX_ATTEMPTS) {
        onError(authenticatePasswordlessUserError || errorCreatePasswordlessUser);
      }
    }
  }, [
    otp,
    createPasswordlessUserResponse,
    authenticatePasswordlessUserResponse,
    errorVerifyUpdateContactDetails,
    authenticatePasswordlessUserError,
    isMobileAndEmail,
    verificationType,
    errorCreatePasswordlessUser,
    buildLogContext,
  ]);

  useEffect(() => {
    // This effect is to handle the response from update contact details API
    if (verifyUpdateContactDetailsResponse) {
      setVerificationCompleted(otp, verificationDetails?.mobile);
      onAuthSuccess();
    }

    if (errorSendUpdateContactDetailsVerification) {
      setError(errorField as FieldPath<FormContactFieldsType>, {
        message: errorSendUpdateContactDetailsVerification?.response?.errors?.[0].message,
      });
    }

    if (errorVerifyUpdateContactDetails) {
      setError('updateOtp', {
        message: errorVerifyUpdateContactDetails?.response?.errors?.[0].message,
      });
    }
  }, [
    otp,
    errorField,
    errorSendUpdateContactDetailsVerification,
    errorVerifyUpdateContactDetails,
    verifyUpdateContactDetailsResponse,
  ]);

  const onResetCodeRequested = () => {
    setFocus('otp');
  };

  const onSubmitUserVerification = (contactDetails: ContactDetailsType) => {
    switch (true) {
      case !contactDetails.otp && !existingCookie:
        datadogRum.addAction(
          'Contact verification request code clicked',
          buildLogContext({
            flow: 'initial',
            verificationType: contactDetails.verificationType,
            hasEmail: !!contactDetails.email,
            hasMobile: !!contactDetails.mobile,
            hasOtp: !!contactDetails.otp,
            hasExistingCookie: !!existingCookie,
          }),
        );
        createPasswordlessUser({
          verificationType: contactDetails.verificationType,
          email: contactDetails.email,
          mobile: contactDetails.mobile,
          financialProductId: financialProductId,
        });

        break;
      case contactDetails.codeAccepted === CodeAcceptanceStatus.NotAccepted:
        authenticatePasswordlessUser({
          verificationType: contactDetails.verificationType,
          email: contactDetails.email,
          mobile: contactDetails.mobile,
          otp: contactDetails.otp,
        });

        break;
      default:
        break;
    }
  };

  const onSubmitContactUpdateVerification = (contactDetails: ContactDetailsType) => {
    switch (true) {
      case (contactDetails.newEmail || contactDetails.mobile || contactDetails.newMobile) &&
        !contactDetails.updateOtpSent:
        datadogRum.addAction(
          'Contact verification request update code clicked',
          buildLogContext({
            flow: 'update',
            verificationType:
              contactDetails.verificationType === VerificationType.Email || isMobileInvalid
                ? VerificationType.Sms
                : VerificationType.Email,
            hasNewEmail: !!contactDetails.newEmail,
            hasNewMobile: !!contactDetails.newMobile,
            hasOtp: !!contactDetails.updateOtp,
          }),
        );

        sendUpdateContactDetailsVerification(
          {
            verificationType:
              // We doing other way around as in verification step so we verify both mobile and email
              contactDetails.verificationType === VerificationType.Email || isMobileInvalid
                ? VerificationType.Sms
                : VerificationType.Email,
            email: contactDetails.newEmail,
            mobile: contactDetails.newMobile,
          },
          { onSuccess: () => setValue('updateOtpSent', true) },
        );

        break;
      case contactDetails.updateOtpSent:
        verifyUpdateContactDetails({
          verificationType:
            // This we doing other way around as in verification step so we verify both mobile and email
            contactDetails.verificationType === VerificationType.Email || isMobileInvalid
              ? VerificationType.Sms
              : VerificationType.Email,
          email: contactDetails.newEmail,
          mobile: contactDetails.newMobile,
          otp: contactDetails.updateOtp,
        });

        break;
      default:
        break;
    }
  };

  const onAuthSuccess = () => {
    const [email, mobile, newEmail, newMobile] = getValues(['email', 'mobile', 'newEmail', 'newMobile']);

    onTracking?.();
    onSuccess?.({ verifiedEmail: newEmail ?? email, verifiedMobile: newMobile ?? mobile });
  };

  const submitContactDetails =
    (codeAccepted === CodeAcceptanceStatus.Email || codeAccepted === CodeAcceptanceStatus.Sms) && !isUniversalLogin
      ? onSubmitContactUpdateVerification
      : onSubmitUserVerification;

  const onSubmitInvalid = (errors: FieldErrors<FormContactFieldsType>) => {
    datadogRum.addAction(
      'Contact verification submit blocked',
      buildLogContext({
        errorFields: Object.keys(errors),
      }),
    );
  };

  if (v2Flow) {
    return (
      <>
        <ContactVerification
          verificationDetails={verificationDetails}
          submitContactDetails={submitContactDetails}
          financialProductId={financialProductId}
          isSubmitting={authenticatePasswordlessLoading || isLoadingUpdateDetails}
          isRequesting={createPasswordlessUserLoading || isRequestLoading}
          isUniversalLogin={isUniversalLogin}
          {...formContext}
        />
        {import.meta.env.DEV && <DevTool control={control} />}
      </>
    );
  }

  return (
    <Container size="small">
      <Stack gap="xxlarge" alignSelf="center">
        <Text>{isMobileAndEmail || isMobileOnly ? CONFIRM_MOBILE : CONFIRM_EMAIL}</Text>
        <ValidationFields
          verificationDetails={verificationDetails}
          isEmailOnly={isEmailOnly}
          isMobileAndEmail={isMobileAndEmail}
          isMobileOnly={isMobileOnly}
          onResetCode={onResetCodeRequested}
          {...formContext}
        />
        <UpdateFields verificationDetails={verificationDetails} isMobileOnly={isMobileOnly} {...formContext} />
        {((isMobileAndEmail && otpSent) || updateOtpSent) &&
          financialProductId !== FINANCIAL_PRODUCT.CBA_RESIDENTIAL && (
            <Alert tone="info">
              <Text size="small">
                By providing this secure code to Brighte you give your electronic consent to the{' '}
                <TextLink href="https://brighte.com.au/support/credit-guide" target="_blank" rel="noreferrer">
                  credit guide
                </TextLink>{' '}
                and{' '}
                <TextLink href="https://brighte.com.au/privacy" target="_blank" rel="noreferrer">
                  privacy policies
                </TextLink>
                . Applications are subject to Brighte's lending criteria.
              </Text>
            </Alert>
          )}
        <Button
          type="submit"
          onClick={handleSubmit(submitContactDetails, onSubmitInvalid)}
          loading={createPasswordlessUserLoading || authenticatePasswordlessLoading || isLoadingUpdateDetails}
        >
          {getContinueLabel(otpSent, updateOtpSent, codeAccepted)}
        </Button>
        <input type="hidden" {...register('verificationType')} />
        <input type="hidden" {...register('otpSent')} />
        <input type="hidden" {...register('codeAccepted')} />
        <input type="hidden" {...register('updateOtpSent')} />
      </Stack>
    </Container>
  );
};

export const getContinueLabel = (otpSent: boolean, updateOtpSent: boolean, codeAccepted: CodeAcceptanceStatus) => {
  if (codeAccepted !== CodeAcceptanceStatus.NotAccepted && updateOtpSent) {
    return CONTINUE;
  }

  //otp for validation is accepted but need to verify new contact info
  if (codeAccepted === CodeAcceptanceStatus.Sms || codeAccepted === CodeAcceptanceStatus.Email) {
    return REQUEST_CODE;
  }

  // otp for validation is sent
  if (otpSent) {
    return CONTINUE;
  }

  return REQUEST_CODE;
};
